import React, { useState, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

export default function HelpHint({ label, text, children }) {
  const id = useId();
  const button = useRef(null);
  const bubble = useRef(null);
  const timer = useRef(null);
  const [position, setPosition] = useState(null);
  const cancelClose = () => clearTimeout(timer.current);
  const close = () => { cancelClose(); setPosition(null); };
  const show = () => {
    cancelClose();
    const r = button.current.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    setPosition({ left: Math.max(12, Math.min(r.left, window.innerWidth - width - 12)), top: r.bottom + 8, width });
  };
  const leave = () => { cancelClose(); timer.current = setTimeout(close, 150); };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!position) return;
    const rect = bubble.current?.getBoundingClientRect();
    if (rect && rect.bottom > window.innerHeight - 12) {
      const top = Math.max(12, window.innerHeight - rect.height - 12);
      if (top !== position.top) setPosition(p => ({ ...p, top }));
    }
    const key = e => { if (e.key === 'Escape') close(); };
    const outside = e => { if (!button.current?.contains(e.target) && !bubble.current?.contains(e.target)) close(); };
    document.addEventListener('keydown', key);
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', key);
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [position]);
  return <>
    <button ref={button} type="button" aria-label={`Подсказка: ${label}`} aria-describedby={position ? id : undefined} aria-expanded={!!position}
      onMouseEnter={show} onMouseLeave={leave} onFocus={show} onBlur={close} onClick={show}
      style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 6, padding: 3, border: 0, background: 'transparent', color: 'var(--text-secondary)', cursor: 'help', borderRadius: 4 }}>
      {children || <HelpCircle size={15} aria-hidden="true" />}
    </button>
    {position && createPortal(<div ref={bubble} id={id} role="tooltip" onMouseEnter={cancelClose} onMouseLeave={leave}
      style={{ position: 'fixed', ...position, boxSizing: 'border-box', maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', zIndex: 10000, padding: '12px 14px', borderRadius: 8, background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', color: 'var(--tooltip-text)', fontSize: 13, lineHeight: 1.5, boxShadow: '0 8px 24px #0006' }}>{text}</div>, document.body)}
  </>;
}

